// The constant chrome for the provider application — a sibling of components/Shell
// (which is patient-funnel-specific: time clock + "no account needed" copy). Same
// visual system: logo header, teal progress bar, centred 640px column, footnote.
import type { ReactNode } from 'react';
import { useIsNarrow } from '../lib/useIsNarrow';

export function ProviderShell({
  progress,
  rightLabel,
  onBack,
  showBack,
  footnote,
  children,
}: {
  progress: number; // 0–1
  rightLabel?: string;
  onBack?: () => void;
  showBack?: boolean;
  footnote?: ReactNode;
  children: ReactNode;
}) {
  const narrow = useIsNarrow();
  return (
    <div style={{ minHeight: '100vh', background: '#fff', display: 'flex', flexDirection: 'column' }}>
      {/* Header */}
      <div>
        <div
          style={{
            maxWidth: 1152,
            margin: '0 auto',
            padding: narrow ? '14px 20px' : '18px 32px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <img src="/expy-man.png" alt="" width={26} height={26} style={{ display: 'block' }} />
            <span style={{ fontSize: 12, fontWeight: 800, letterSpacing: '.14em', textTransform: 'uppercase' }}>
              Expy Health
            </span>
            <span
              style={{
                fontSize: 11,
                fontWeight: 800,
                letterSpacing: '.1em',
                textTransform: 'uppercase',
                color: 'var(--teal-deep)',
                background: 'var(--teal-tint)',
                borderRadius: 6,
                padding: '3px 8px',
              }}
            >
              For physical therapists
            </span>
          </div>
          {rightLabel && (
            <span style={{ fontSize: 13, fontWeight: 700, color: 'var(--slate)' }}>{rightLabel}</span>
          )}
        </div>
        {/* Progress bar */}
        <div style={{ height: 4, background: 'var(--teal-tint)' }}>
          <div
            style={{
              width: `${Math.max(4, progress * 100)}%`,
              height: '100%',
              background: 'var(--teal)',
              borderRadius: '0 2px 2px 0',
              transition: 'width .3s ease',
            }}
          />
        </div>
      </div>

      {/* Body — centred column */}
      <div style={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
        <div
          className="fade-in"
          style={{
            maxWidth: 640,
            width: '100%',
            margin: '0 auto',
            padding: narrow ? '32px 20px 60px' : '48px 24px 32px',
          }}
        >
          {showBack && onBack && (
            <button onClick={onBack} style={{ display: 'flex', alignItems: 'center', gap: 7, marginBottom: 18 }}>
              <svg width={13} height={13} viewBox="0 0 14 14" fill="none" aria-hidden>
                <path d="M9 3L5 7l4 4" stroke="var(--teal-deep)" strokeWidth={1.8} strokeLinecap="round" strokeLinejoin="round" />
              </svg>
              <span style={{ fontSize: 13, fontWeight: 700, color: 'var(--teal-deep)' }}>Back</span>
            </button>
          )}
          {children}
        </div>
      </div>

      {/* Footnote */}
      {footnote && (
        <div style={{ maxWidth: 640, width: '100%', margin: '0 auto', padding: '0 24px 36px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 9 }}>
            <svg width={14} height={14} viewBox="0 0 16 16" fill="none" aria-hidden>
              <path d="M3.5 8.4l2.8 2.8L12.5 5" stroke="var(--teal-deep)" strokeWidth={1.8} strokeLinecap="round" strokeLinejoin="round" />
            </svg>
            <span style={{ fontSize: 13, fontWeight: 600, color: 'var(--teal-deep)' }}>{footnote}</span>
          </div>
        </div>
      )}
    </div>
  );
}

// Shared header block for a section/screen (mirrors Question.tsx's Header).
export function ScreenHeader({ eyebrow, title, subtitle }: { eyebrow?: string; title: string; subtitle?: string }) {
  return (
    <div style={{ marginBottom: 24 }}>
      {eyebrow && (
        <div style={{ fontSize: 11, fontWeight: 800, color: 'var(--teal-deep)', textTransform: 'uppercase', letterSpacing: '.14em' }}>
          {eyebrow}
        </div>
      )}
      <div
        className="pretty"
        style={{ fontSize: 34, fontWeight: 800, lineHeight: 1.16, letterSpacing: '-.015em', marginTop: eyebrow ? 12 : 0 }}
      >
        {title}
      </div>
      {subtitle && (
        <div className="pretty" style={{ fontSize: 16, color: 'var(--slate)', fontWeight: 500, lineHeight: 1.5, marginTop: 10 }}>
          {subtitle}
        </div>
      )}
    </div>
  );
}
